/**
 * Web component: Flip clock (flip-clock)
 * -----------------------------------------------------------------------------
 * Flip Clock to count down to set end date and hide given elements.
 *
 */
/* eslint no-unused-vars: ['error', { 'argsIgnorePattern': 'component' }] */
import './flip-clock.scss';

import { vueReady } from '~/core/runners';
import classes from '~/config/classes';

/**
 * DOM selectors.
 */
const selectors = {
  countdownCard: '[js-flip-clock="countdownCard"]',
  countdown: '[js-flip-clock="countdown"]',
  daysCardsWrapper: '[js-flip-clock="days"]',
  hoursCardsWrapper: '[js-flip-clock="hours"]',
  minutesCardsWrapper: '[js-flip-clock="minutes"]',
  secondsCardsWrapper: '[js-flip-clock="seconds"]',
  daysDivider: '[js-flip-clock="divider-days"]',
  hoursDivider: '[js-flip-clock="divider-hours"]',
  minutesDivider: '[js-flip-clock="divider-minutes"]',
};

/**
 * Create new <flip-clock> custom element.
 */
class FlipClock extends HTMLElement {
  constructor() {
    super();
    this.showSeconds = typeof this.dataset.showSeconds !== 'undefined';
  }

  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    this.countdownTimer();
  }

  /**
   * Get time remaining until end time.
   * @param {String} end_time - End time.
   * @param {Boolean} showTotalHours - Show total hours instead of days + hours.
   * @returns {Object} - Time remaining.
   *
   */
  getTimeRemaining(endTime, showTotalHours = false) {
    const remainingTime = Date.parse(endTime) - Date.parse(new Date());
    return {
      total: remainingTime,
      days: Math.floor(remainingTime / (1000 * 60 * 60 * 24)),
      hours: showTotalHours
        ? Math.floor(remainingTime / (1000 * 60 * 60))
        : Math.floor((remainingTime / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((remainingTime / 1000 / 60) % 60),
      seconds: Math.floor((remainingTime / 1000) % 60),
    };
  }

  /**
   * Handle countdown timer.
   */
  countdownTimer() {
    const countdownElement = this.querySelector(selectors.countdown);
    const refreshSite = countdownElement.dataset.refreshSite;
    const refreshDelay =
      Number.parseInt(countdownElement.dataset.refreshDelay) || 0;
    const showTotalHours = countdownElement.dataset.showTotalHours === 'true';
    const countdownUnits = [
      {
        unit: 'days',
        selector: selectors.daysCardsWrapper,
        divider: selectors.daysDivider,
      },
      {
        unit: 'hours',
        selector: selectors.hoursCardsWrapper,
        divider: selectors.hoursDivider,
      },
      {
        unit: 'minutes',
        selector: selectors.minutesCardsWrapper,
        divider: selectors.minutesDivider,
      },
      {
        unit: 'seconds',
        selector: selectors.secondsCardsWrapper,
      },
    ];
    const endTime = countdownElement.dataset.countdownDate;
    const card = this.querySelector(selectors.countdownCard);
    const cards = this.querySelectorAll(selectors.countdownCard);

    // Check if countdown has already expired to prevent reload loops
    const initialCountdown = this.getTimeRemaining(endTime, showTotalHours);
    if (initialCountdown.total <= 0) {
      this.hideCampaignInfoOrCountdown('countdown');
      if (card?.classList.contains('is-loading')) {
        for (const elem of cards) {
          elem.classList.remove('is-loading');
        }
      }
      return;
    }

    const countdownInterval = setInterval(() => {
      const countdown = this.getTimeRemaining(endTime, showTotalHours);

      if (countdown.total <= 0) {
        clearInterval(countdownInterval);
        this.hideCampaignInfoOrCountdown('countdown');

        if (card.classList.contains('is-loading')) {
          for (const elem of cards) {
            elem.classList.remove('is-loading');
          }
        }

        if (refreshSite === 'true') {
          setTimeout(() => {
            location.reload();
          }, refreshDelay);
        }
        return;
      }

      if (card.classList.contains('is-loading')) {
        for (const elem of cards) {
          elem.classList.remove('is-loading');
        }
      }

      this.updateCountdown(countdownElement, countdownUnits, endTime);
    }, 1000);
  }

  /**
   * Update countdown timer.
   * @param {Element} countdownElement - Countdown element.
   * @param {Array} countdownUnits - Countdown units.
   * @param {String} endTime - End time.
   */
  updateCountdown(countdownElement, countdownUnits, endTime) {
    const showTotalHours = countdownElement.dataset.showTotalHours === 'true';
    const countdown = this.getTimeRemaining(endTime, showTotalHours);
    const showDigits = countdownElement.dataset.showDigits;
    let countShowingDigits = 0;

    // Check if we should show minutes:seconds when less than 1 hour remaining
    const showMinutesAndSeconds = showTotalHours && countdown.hours < 1;

    countdownUnits.forEach(({ unit, selector, divider }) => {
      const countdownUnit = countdownElement.querySelector(selector);
      const unitDivider = divider
        ? countdownElement.querySelector(divider)
        : null;

      let continueCondition = false;
      switch (unit) {
        case 'days':
          // Hide days when showing total hours
          continueCondition =
            countdownUnit !== null &&
            !showTotalHours &&
            countdown[unit] > 0 &&
            countShowingDigits < 2;
          break;
        case 'minutes':
        case 'seconds':
          // Show minutes/seconds when less than 1 hour remains OR when not in showTotalHours mode
          continueCondition =
            countdownUnit !== null &&
            (this.showSeconds ||
              showMinutesAndSeconds ||
              countShowingDigits < showDigits);
          break;
        case 'hours':
          // Show hours when >= 1 hour remains in showTotalHours mode, or normally
          continueCondition =
            countdownUnit !== null &&
            ((showTotalHours && !showMinutesAndSeconds) ||
              countdown.days >= 1 ||
              countdown.hours > 0) &&
            countShowingDigits < 2;
          break;
        default:
          continueCondition =
            countdownUnit !== null &&
            countdown[unit] > 0 &&
            countShowingDigits < 2;
      }

      if (continueCondition) {
        const formattedValue = `0${countdown[unit]}`.slice(-2);
        countdownUnit.querySelectorAll(selectors.countdownCard)[0].innerText =
          formattedValue[0];
        countdownUnit.querySelectorAll(selectors.countdownCard)[1].innerText =
          formattedValue[1];
        countdownUnit.parentElement.classList.remove(classes.hidden);
        unitDivider?.classList.remove(classes.hidden);
        countShowingDigits++;
      } else if (countdownUnit !== null) {
        countdownUnit.parentElement.classList.add(classes.hidden);
        unitDivider?.classList.add(classes.hidden);
      }
    });
  }

  /**
   * Show/hide campaign elements after countdown ends.
   * @param {String} hideContentType - Hide content options
   * type 'countdown' or 'campaign'.
   */
  hideCampaignInfoOrCountdown(hideContentType) {
    const countdownWrapper = this;
    const toggleSelector = this.dataset.toggleClasses;

    // Only query for elements if a valid selector is provided
    const teaserInfo = toggleSelector?.trim()
      ? document.querySelectorAll(toggleSelector)
      : [];

    if (hideContentType === 'countdown') {
      countdownWrapper.classList.add(classes.hidden);

      teaserInfo.forEach((element) => {
        element.classList.remove(classes.hidden);
      });
    } else if (hideContentType === 'campaign') {
      teaserInfo.forEach((element) => {
        element.classList.add(classes.hidden);
      });

      countdownWrapper.classList.remove(classes.hidden);
    }
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('flip-clock')) {
    return;
  }

  customElements.define('flip-clock', FlipClock);
});
